import { useEffect, useState } from "react";
import { Download, Trash2, Tag, Plus, X, MessageSquare, FileDown, User, CheckSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import Pager from "@/components/site/Pager";

const PAGE_SIZE = 25;

const STATUSES = ["new", "screening", "shortlisted", "interview", "offered", "hired", "rejected"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  screening: "bg-amber-100 text-amber-700",
  shortlisted: "bg-purple-100 text-purple-700",
  interview: "bg-indigo-100 text-indigo-700",
  offered: "bg-emerald-100 text-emerald-700",
  hired: "bg-green-100 text-green-800",
  rejected: "bg-rose-100 text-rose-700",
};

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [recruiters, setRecruiters] = useState([]);

  const load = () => {
    api.get("/admin/applications", { params: { q: q || undefined, status: status || undefined, tag: tag || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setSelected(new Set()); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, tag, page]);
  useEffect(() => { setPage(1); }, [q, status, tag]);
  useEffect(() => { api.get("/admin/recruiters").then((r) => setRecruiters(r.data.items || [])); }, []);

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((a) => a.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const bulk = async (payload) => {
    if (selected.size === 0) { toast.error("Select at least one row"); return; }
    try {
      await api.post("/admin/applications/bulk", { application_ids: Array.from(selected), ...payload });
      toast.success(`Applied to ${selected.size} record${selected.size === 1 ? "" : "s"}`);
      load();
    } catch { toast.error("Bulk action failed"); }
  };

  const updateStatus = async (id, s) => {
    try { await api.patch(`/admin/applications/${id}/status`, { status: s }); toast.success("Status updated"); load(); }
    catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admin/applications/${id}`);
    toast.success("Deleted"); load();
  };

  const downloadResume = async (a) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/applications/${a.id}/resume`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = a.resume_filename || "resume"; link.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const token = getToken();
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const res = await fetch(`${API_BASE}/admin/exports/applications?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `applications-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  return (
    <div className="space-y-6" data-testid="admin-applications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Applications</h1>
          <p className="text-sm text-slate-500">{total} application{total === 1 ? "" : "s"}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="applications-export-csv"><FileDown className="mr-1 h-4 w-4" />Export CSV</Button>
      </div>

      <div className="grid gap-2 md:grid-cols-12">
        <Input placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} className="md:col-span-5" data-testid="applications-search" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="md:col-span-3" data-testid="applications-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Tag filter…" value={tag} onChange={(e) => setTag(e.target.value)} className="md:col-span-4" data-testid="applications-tag-filter" />
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="bulk-bar">
          <CheckSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-slate-700">{selected.size} selected</span>
          <Select onValueChange={(v) => bulk({ status: v })}>
            <SelectTrigger className="h-8 w-40" data-testid="bulk-status"><SelectValue placeholder="Change status…" /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => { const r = recruiters.find((x) => x.id === v); bulk({ recruiter_id: v, recruiter_name: r?.name }); }}>
            <SelectTrigger className="h-8 w-44" data-testid="bulk-assign"><SelectValue placeholder="Assign recruiter…" /></SelectTrigger>
            <SelectContent>{recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          <BulkTagInput onAdd={(tags) => bulk({ add_tags: tags })} />
          <Button variant="outline" size="sm" onClick={() => bulk({ archive: true })}>Archive</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-3"><Checkbox checked={selected.size === items.length && items.length > 0} onCheckedChange={toggleAll} data-testid="bulk-toggle-all" /></th>
              <th className="px-4 py-3">Candidate</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Exp</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Tags</th>
              <th className="px-4 py-3">Applied</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className={`border-b last:border-0 hover:bg-slate-50 ${selected.has(a.id) ? "bg-primary/5" : ""}`}>
                <td className="px-3 py-3"><Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleOne(a.id)} data-testid={`bulk-toggle-${a.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">
                    {a.candidate_name}
                    {a.duplicate_of && <Badge className="ml-2 bg-amber-100 text-amber-700">Duplicate</Badge>}
                    {a.assigned_recruiter_name && <Badge className="ml-2 bg-secondary/15 text-secondary">{a.assigned_recruiter_name}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">{a.email} · {a.phone}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{a.job_title || <span className="text-slate-400">General</span>}</td>
                <td className="px-4 py-3 text-slate-700">{a.experience_years} yr</td>
                <td className="px-4 py-3">
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger className={`h-8 w-32 ${STATUS_COLORS[a.status] || ""}`} data-testid={`app-status-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(a.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="bg-slate-100 text-slate-700">{t}</Badge>)}
                    {(a.tags?.length || 0) > 3 && <Badge variant="secondary" className="bg-slate-100">+{a.tags.length - 3}</Badge>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.applied_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Link to={`/admin/candidates/${encodeURIComponent(a.email)}`} data-testid={`app-360-${a.id}`}><Button variant="ghost" size="sm"><User className="h-3.5 w-3.5" /></Button></Link>
                    <Button variant="ghost" size="sm" onClick={() => setActive(a)} data-testid={`app-open-${a.id}`}><MessageSquare className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadResume(a)} data-testid={`app-download-${a.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(a.id)} data-testid={`app-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No applications yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-apps-pager" />

      {active && <ApplicationDialog app={active} onClose={() => setActive(null)} onUpdated={load} />}
    </div>
  );
}

function ApplicationDialog({ app, onClose, onUpdated }) {
  const [detail, setDetail] = useState(app);
  const [noteText, setNoteText] = useState("");
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    api.get(`/admin/applications/${app.id}`).then((r) => setDetail(r.data));
  }, [app.id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/admin/applications/${app.id}/notes`, { text: noteText.trim() });
    setNoteText("");
    const r = await api.get(`/admin/applications/${app.id}`);
    setDetail(r.data);
    onUpdated?.();
    toast.success("Note added");
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    const tags = Array.from(new Set([...(detail.tags || []), t]));
    await api.put(`/admin/applications/${app.id}/tags`, { tags });
    setDetail({ ...detail, tags });
    setTagInput("");
    onUpdated?.();
  };

  const removeTag = async (t) => {
    const tags = (detail.tags || []).filter((x) => x !== t);
    await api.put(`/admin/applications/${app.id}/tags`, { tags });
    setDetail({ ...detail, tags });
    onUpdated?.();
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="application-dialog">
        <DialogHeader>
          <DialogTitle>{detail.candidate_name}</DialogTitle>
          <DialogDescription>{detail.email} · {detail.phone}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><span className="text-slate-500">Role:</span> {detail.job_title || "General"}</div>
            <div><span className="text-slate-500">Experience:</span> {detail.experience_years} yrs</div>
            <div><span className="text-slate-500">Company:</span> {detail.current_company || "—"}</div>
            <div><span className="text-slate-500">Notice:</span> {detail.notice_period || "—"}</div>
            <div><span className="text-slate-500">Location:</span> {detail.location || "—"}</div>
            <div><span className="text-slate-500">Source:</span> {detail.source}{detail.referrer_name ? ` (${detail.referrer_name})` : ""}</div>
          </div>

          {detail.cover_letter && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cover</div>
              <p className="mt-1 whitespace-pre-line rounded bg-slate-50 p-3 text-slate-700">{detail.cover_letter}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500"><Tag className="mr-1 inline h-3 w-3" />Tags</div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {(detail.tags || []).map((t) => (
                <Badge key={t} className="gap-1 bg-slate-100 text-slate-700">
                  {t} <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              <div className="flex gap-1">
                <Input className="h-8 w-32" placeholder="add tag" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} data-testid="app-tag-input" />
                <Button size="sm" variant="outline" onClick={addTag} data-testid="app-tag-add"><Plus className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal notes</div>
            <div className="mt-2 space-y-2">
              {(detail.notes || []).map((n, i) => (
                <div key={i} className="rounded border border-slate-200 bg-slate-50 p-2 text-sm">
                  <div className="text-slate-700">{n.text}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">{n.by} · {new Date(n.at).toLocaleString()}</div>
                </div>
              ))}
              {(detail.notes?.length || 0) === 0 && <div className="text-slate-400">No notes yet.</div>}
            </div>
            <div className="mt-2 flex gap-2">
              <Textarea rows={2} placeholder="Add a private note (visible to admins only)" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="app-note-input" />
              <Button onClick={addNote} className="self-end" data-testid="app-note-add">Add</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BulkTagInput({ onAdd }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-1">
      <Input className="h-8 w-36" placeholder="Add tag(s)" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => {
        if (e.key === "Enter" && v.trim()) { onAdd(v.split(",").map((x) => x.trim()).filter(Boolean)); setV(""); }
      }} data-testid="bulk-tag-input" />
      <Button size="sm" variant="outline" onClick={() => { if (v.trim()) { onAdd(v.split(",").map((x) => x.trim()).filter(Boolean)); setV(""); } }}><Plus className="h-3 w-3" /></Button>
    </div>
  );
}
